import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Linkedin, Globe2 } from "lucide-react";
import { siteMeta, navLinks } from "../data/mock";

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="relative pt-24 pb-10" style={{ background: "#0a0908", borderTop: "1px solid rgba(201,169,97,0.12)" }}>
      <div className="container-xl">
        {/* Big CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="grid lg:grid-cols-[2fr_1fr] gap-10 items-end pb-20 border-b border-[rgba(201,169,97,0.15)]"
        >
          <h2 className="font-display text-[clamp(44px,8vw,140px)] leading-[0.95] text-[var(--cream)] balance">
            Let's make something <span className="italic gold-text">unforgettable.</span>
          </h2>
          <Link to="/contact" className="btn-primary justify-self-start lg:justify-self-end" data-cursor="start">
            Start a project
            <ArrowUpRight size={16} />
          </Link>
        </motion.div>

        {/* Grid */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center mb-10">
              <div className="h-24 w-auto overflow-hidden">
                <img src={siteMeta.logo} alt="Meraki Logo" className="h-full w-auto object-contain" />
              </div>
            </div>
            <div className="mt-6 text-[13px] leading-relaxed text-[var(--cream-muted)] max-w-sm space-y-3">
              <p>
                A multidisciplinary creative studio blending storytelling, branding, media, and digital innovation to build work that resonates.
              </p>
              <p>
                At Meraki Solutions, we believe the strongest brands are built through clarity, emotion, and experience. We bring together strategy, storytelling, design, media, and technology to create meaningful work that connects with people and leaves a lasting impact.
              </p>
            </div>
            <div className="mt-8 flex gap-3">
              <Social href={siteMeta.socials.website} label="Website"><Globe2 size={16} /></Social>
              <Social href={siteMeta.socials.instagram} label="Instagram"><Instagram size={16} /></Social>
              <Social href={siteMeta.socials.linkedin} label="LinkedIn"><Linkedin size={16} /></Social>
            </div>
          </div>

          <div>
            <div className="font-impact text-[10px] tracking-[0.35em] text-[var(--gold)] uppercase">Studio</div>
            <ul className="mt-5 space-y-3">
              {navLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.href} className="text-[14px] text-[var(--cream-muted)] hover:text-[var(--gold)] transition-colors link-reveal">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-impact text-[10px] tracking-[0.35em] text-[var(--gold)] uppercase">Services</div>
            <ul className="mt-5 space-y-3">
              {navLinks.find(l => l.label === "Services")?.sublinks.map((sub) => (
                <li key={sub.href}>
                  <Link to={sub.href} className="text-[14px] text-[var(--cream-muted)] hover:text-[var(--gold)] transition-colors link-reveal">
                    {sub.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <div className="font-impact text-[10px] tracking-[0.35em] text-[var(--gold)] uppercase">Connect</div>
            <ul className="mt-5 space-y-4 text-[14px]">
              <li>
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1">Email</div>
                <a href={`mailto:${siteMeta.email}`} className="text-[var(--cream)] hover:text-[var(--gold)] transition-colors">{siteMeta.email}</a>
              </li>
              <li>
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1">Call</div>
                <div className="text-[var(--cream-muted)]">{siteMeta.phone}</div>
              </li>
              <li>
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1">Visit</div>
                <div className="text-[var(--cream-muted)] leading-relaxed">{siteMeta.location}</div>
              </li>
            </ul>
          </div>
        </div>

        {/* Huge wordmark */}
        <div className="mt-20 select-none">
          <div
            className="font-display italic text-[clamp(80px,22vw,320px)] leading-[0.85] tracking-tighter uppercase"
            style={{
              background: "linear-gradient(180deg, rgba(228,199,122,0.25) 0%, rgba(228,199,122,0.02) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}
          >
            MERAKI.
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-6 pt-6 border-t border-[rgba(201,169,97,0.1)]">
          <div className="flex flex-wrap items-center gap-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--muted)]">
              © {year} MERAKI SOLUTIONS. Crafted with care.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <Link to="/privacy" className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)] hover:text-[var(--gold)] transition-colors link-reveal">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)] hover:text-[var(--gold)] transition-colors link-reveal">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Social = ({ href, label, children }) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={label}
    className="btn-icon"
    data-cursor="follow"
  >
    {children}
  </a>
);

export default Footer;
